/**
 * /try WebSocket Streaming Client (Browser).
 *
 * WHAT THIS FILE DOES:
 * - Opens a WebSocket to the backend streaming gateway using wsUrl + wsToken.
 * - Parses StoryStream JSON frames and forwards them to a caller callback.
 * - Ignores heartbeat frames (ping/pong).
 *
 * WHY THIS EXISTS:
 * - /try needs time-to-first-slide, not time-to-ready.
 * - Streaming frames let us mount the unified reader immediately and render
 *   progressively as the summarizer emits partial slides.
 *
 * CALLED BY:
 * - src/client/try-ui.ts after ingest returns contentId + ws credentials
 *
 * @file src/client/try-ws.ts
 */

import { WebTryError } from './try-shared.js';

/**
 * Parsed StoryStream message shape (best-effort; backend is the source of truth).
 */
export type StoryStreamMessage = Record<string, unknown>;

export interface TryWebSocketOptions {
  contentId: string;
  wsUrl: string;
  wsToken: string;
  onMessage: (message: StoryStreamMessage) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
}

export interface TryWebSocketConnection {
  close: () => void;
}

function isWebSocketUrl(value: string): boolean {
  return /^wss?:\/\//i.test(value);
}

function parseWebSocketFrame(rawData: unknown, fallbackContentId: string): StoryStreamMessage | null {
  if (typeof rawData !== 'string' && !(rawData instanceof ArrayBuffer)) {
    return null;
  }

  const text = typeof rawData === 'string' ? rawData : new TextDecoder().decode(rawData);
  if (text === 'pong' || text === 'ping') {
    return null;
  }

  try {
    const message = JSON.parse(text) as StoryStreamMessage;
    if (message && typeof message === 'object') {
      if (!('contentId' in message) && fallbackContentId) {
        message.contentId = fallbackContentId;
      }
      return message;
    }
    return null;
  } catch {
    return null;
  }
}

function buildTokenizedWebSocketUrl(wsUrl: string, wsToken: string): string {
  const url = new URL(wsUrl);
  url.searchParams.set('token', wsToken);
  return url.toString();
}

/**
 * Opens a WebSocket connection and forwards parsed frames to onMessage.
 */
export function connectTryWebSocket(options: TryWebSocketOptions): TryWebSocketConnection {
  if (!options.contentId) {
    throw new WebTryError('ws_content_id_required');
  }
  if (!options.wsUrl || !isWebSocketUrl(options.wsUrl)) {
    throw new WebTryError('ws_url_invalid', { details: { wsUrl: options.wsUrl } });
  }
  if (!options.wsToken) {
    throw new WebTryError('ws_token_missing');
  }

  const fullUrl = buildTokenizedWebSocketUrl(options.wsUrl, options.wsToken);
  const socket = new WebSocket(fullUrl);

  socket.onopen = () => {
    options.onOpen?.();
  };

  socket.onmessage = (event) => {
    const parsed = parseWebSocketFrame(event?.data, options.contentId);
    if (!parsed) {
      return;
    }
    options.onMessage(parsed);
  };

  socket.onclose = (event) => {
    options.onClose?.(event);
  };

  socket.onerror = (event) => {
    options.onError?.(event);
  };

  return {
    close: () => {
      try {
        socket.close(1000, 'Client close');
      } catch {
        // Best-effort.
      }
    }
  };
}
